import {
  ReportBuilderDataSourceId,
  TemplateScope,
} from '@principle-theorem/principle-core/interfaces';
import { FactTables } from '../../models/fact-tables';
import { toMeasureBuilderData } from '../../models/measure-properties';
import { ReportBuilderColumns } from '../../report-builder-columns';
import { ChartFilterDefinition } from '../charts/chart-filter-definition';
import { ReportBuilderCharts } from '../charts/report-builder-charts';
import { ReportBuilderCommonCharts } from '../charts/report-builder-common-charts';
import { ReportBuilderDataSourceInstance } from '../report-builder-data-source-instance';
import { ReportBuilderGroups } from '../report-builder-groups';
import { ReportBuilderScopeDataAccessors } from '../report-builder-scope-data-accessors';

export class InvoicesDataSource extends ReportBuilderDataSourceInstance {
  override readonly id = ReportBuilderDataSourceId.Invoices;
  override readonly name = 'Invoices';
  override readonly description =
    'One result per Invoice within Principle. This includes information about the practice and patient associated with each invoice.';
  override readonly factTable = FactTables.invoiceEvent.latestEvent;
  override readonly rowItemName = 'Invoice';

  override readonly queryableTimestamps = [
    { property: this.factTable.invoice.createdAt },
    { property: this.factTable.invoice.issuedAt },
    { property: this.factTable.invoice.paidAt },
    { property: this.factTable.timestamp },
  ];

  scopes = [TemplateScope.Invoice, TemplateScope.Patient];
  scopeDataAccessor = ReportBuilderScopeDataAccessors.build([
    this.factTable.invoice,
    this.factTable.patient,
  ]);

  override readonly groupByOptions = [
    ReportBuilderGroups.toGroup('Common', [
      ReportBuilderGroups.toOption(this.factTable.invoice.status),
      ReportBuilderGroups.toOption(this.factTable.invoice.issuedAt),
      ReportBuilderGroups.toOption(this.factTable.invoice.paidAt),
      ReportBuilderGroups.toOption(this.factTable.practice.name),
      ReportBuilderGroups.toOption(this.factTable.brand.name),
      ReportBuilderGroups.toOption(this.factTable.count),
    ]),
    ReportBuilderGroups.invoice(this.factTable.invoice),
    ReportBuilderGroups.patient(this.factTable.patient),
  ];

  override readonly availableColumns = [
    ReportBuilderColumns.toColumnGroup('Invoice', [
      this.factTable.isOverdue,
      this.factTable.paidOnCheckout,
      this.factTable.paidToDate,
      this.factTable.amountRemaining,
      this.factTable.subTotal,
      this.factTable.tax,
      this.factTable.practice.name,
      this.factTable.completedTransactions,
      this.factTable.amountPayable,
      ...ReportBuilderColumns.invoice(this.factTable.invoice),
      this.factTable.link,
    ]),
    ReportBuilderColumns.toColumnGroup('Patient', [
      ...ReportBuilderColumns.patient(this.factTable.patient),
      this.factTable.patientLink,
    ]),
  ];

  override readonly defaultColumns = ReportBuilderColumns.toReferences([
    this.factTable.patient.name,
    this.factTable.invoice.createdAt,
    this.factTable.invoice.total,
    this.factTable.invoice.status,
    this.factTable.link,
  ]);

  override readonly availableCharts = [
    ReportBuilderCharts.predefined(
      'overview.brand.price',
      this.factTable.brand.name,
      ChartFilterDefinition.numberSummary(
        'Total Price',
        this.factTable.brand.name,
        [toMeasureBuilderData(this.factTable.total)]
      )
    ),
    ReportBuilderCharts.predefined(
      'overview.practice',
      this.factTable.practice.name,
      ChartFilterDefinition.pie('Practice', this.factTable.practice.name, [
        toMeasureBuilderData(this.factTable.total),
      ])
    ),
    ReportBuilderCharts.predefined(
      'overview.totalInvoiced',
      this.factTable.invoice.createdAt,
      ChartFilterDefinition.columnOverTime(
        'Total Invoiced',
        this.factTable.invoice.createdAt,
        [toMeasureBuilderData(this.factTable.total)]
      )
    ),
    ...ReportBuilderCommonCharts.patient(
      this.factTable.patient,
      this.factTable.total
    ),
    ...ReportBuilderCommonCharts.invoice(
      this.factTable.invoice,
      this.factTable.total
    ),
  ];
}
